// src/composables/useFeatureFlag.ts
import { computed, effectScope, getCurrentInstance, inject, ref, watch } from 'vue'
import { useGeneratedFeatureFlagStore } from '@/stores/generated/FeatureFlagStore.ts'
import { useAuthStore } from '@/stores'
import { FeatureFlagDefaultRead } from '@/api'

let globalFeatureFlagsInstance: ReturnType<typeof createFeatureFlags> | null = null

function createFeatureFlags() {
    const store = useGeneratedFeatureFlagStore()
    const authStore = useAuthStore()

    const isReady = ref(false)
    const isInitializing = ref(false)
    const allFeatureFlags = ref<FeatureFlagDefaultRead[]>([])

    const scope = effectScope(true)
    const flagMap = scope.run(() =>
        computed(() => {
            const result = new Map<string, boolean>()
            for (const flag of allFeatureFlags.value ?? []) {
                result.set(flag.name!, flag.enabled!)
            }
            return result
        })
    )

    const loading = computed(() => store.loading || isInitializing.value)

    const loadFlags = async () => {
        isInitializing.value = true
        try {
            const result = await store.findAll({}, { forceRefresh: true })
            allFeatureFlags.value = result?.value || []
            isReady.value = true
            console.log('[FeatureFlags] Loaded:', allFeatureFlags.value)
        } catch (err) {
            console.error('[FeatureFlags] Load error', err)
            allFeatureFlags.value = []
            throw err
        } finally {
            isInitializing.value = false
        }
    }

    const init = async () => {
        if (isReady.value || isInitializing.value) return
        const isLoggedIn = await authStore.checkIfAlreadyLoggedIn()

        if (!isLoggedIn) {
            console.log('[FeatureFlags] Waiting for login...')
            return new Promise<void>((resolve) => {
                const stop = watch(
                    () => authStore.token,
                    async (token) => {
                        if (token) {
                            stop()
                            await loadFlags()
                            resolve()
                        }
                    }
                )
            })
        }

        await loadFlags()
    }

    const refresh = async () => loadFlags()

    const isEnabled = (name: string): boolean =>
        allFeatureFlags.value.find(f => f.name === name)?.enabled ?? false

    const getFlag = (name: string) =>
        allFeatureFlags.value.find(flag => flag.name === name) || null

    const getEnabledFlags = () =>
        Array.from(flagMap?.value?.entries() ?? [])
            .filter(([, enabled]) => enabled)
            .map(([name]) => name)

    const getStats = () => {
        const total = flagMap?.value?.size ?? 0
        const enabled = getEnabledFlags().length
        return { total, enabled, disabled: total - enabled }
    }

    const changeEnable = async (id: string, enabled: boolean) => {
        const resp = await store.putId({ path: { id }, body: { enabled } })
        await refresh()
        return resp
    }

    const changeNotes = async (id: string, notes: string) => {
        await store.putId({ path: { id }, body: { notes } })
        await refresh()
    }

    const addFlag = async (flag: FeatureFlagDefaultRead) => {
        await store.post({ body: flag })
        await refresh()
    }

    const deleteFlag = async (id: number) => {
        await store.delete({ path: { id: id.toString() } })
        await refresh()
    }

    return {
        isReady,
        loading,
        allFeatureFlags,
        init,
        refresh,
        isEnabled,
        getFlag,
        getEnabledFlags,
        getStats,
        changeEnable,
        changeNotes,
        addFlag,
        deleteFlag
    }
}

export function useFeatureFlags() {
    const instance = getCurrentInstance()
    if (instance) {
        const injected = inject('featureFlags', null)
        if (injected) return injected
    }

    if (!globalFeatureFlagsInstance) {
        globalFeatureFlagsInstance = createFeatureFlags()
    }

    return globalFeatureFlagsInstance
}

export default useFeatureFlags
